import { useState } from 'react';
import { ArrowRight, X } from 'lucide-react';

// Links into the nordic3d.us shop. The shop reads `profile` to link the new card to this existing
// HexLink profile (no second setup fee) and pre-fills the engraving details.
export const SHOP = 'https://nordic3d.us';
const METAL_PRICE = 20; // keep in step with metalCard.price in nordic3d.us/public/assets/js/config.js

export function shopUrl(path, medium, extra = {}) {
    const u = new URL(path, SHOP);
    Object.entries(extra).forEach(([k, val]) => val && u.searchParams.set(k, val));
    u.searchParams.set('utm_source', 'hexlink_dashboard');
    u.searchParams.set('utm_medium', medium);
    return u.toString();
}

export function metalUrl(user, medium) {
    const p = user?.profile || {};
    return shopUrl('/product.html', medium, {
        handle: 'hexlink-metal',
        profile: user?.cardUuid,
        name: cleanName(p.name),
        title: p.jobTitle,
        company: p.organization,
        utm_campaign: 'metal_upgrade',
    });
}

function hexMesh() {
    const r = 18;
    const w = Math.sqrt(3) * r;
    const out = [];
    for (let row = 0; row < 7; row++) {
        for (let col = 0; col < 7; col++) {
            const cx = col * w + (row % 2 ? w / 2 : 0);
            const cy = row * r * 1.5;
            const pts = Array.from({ length: 6 }, (_, i) => {
                const a = (Math.PI / 180) * (60 * i - 30);
                return `${(cx + (r - 3) * Math.cos(a)).toFixed(1)},${(cy + (r - 3) * Math.sin(a)).toFixed(1)}`;
            }).join(' ');
            out.push(<polygon key={`${row}-${col}`} points={pts} />);
        }
    }
    return out;
}

// The user's own name "engraved" on a black stainless card.
export function MetalCardArt({ name, title }) {
    return (
        <div className="n3d-metal" aria-hidden="true">
            <svg className="n3d-metal-hex" viewBox="0 0 200 200" fill="none" strokeWidth="2.4">
                {hexMesh()}
            </svg>
            <span className="n3d-metal-logo">HEXLINK</span>
            <span className="n3d-metal-name">{name || 'Your Name'}</span>
            <span className="n3d-metal-title">{title || 'Your Title'}</span>
            <svg className="n3d-metal-nfc" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M8.5 7.5a6 6 0 010 9M12 5a9.5 9.5 0 010 14M15.5 2.5a13 13 0 010 19M5 10a2.5 2.5 0 010 4" />
            </svg>
        </div>
    );
}

const cleanName = (n) => (n || '').replace(/\s*["“][^"”]*["”]\s*/g, ' ').replace(/\s+/g, ' ').trim();
const subtitle = (p = {}) => [p.jobTitle, p.organization].filter(Boolean).join(' · ');

// Sidebar version (desktop)
export function MetalUpgradeCard({ user }) {
    const p = user?.profile || {};
    return (
        <section className="n3d-promo rounded-2xl p-4" aria-labelledby="metal-promo-title">
            <span className="n3d-pill">
                <span className="n3d-dot" />
                New
            </span>
            <div className="mt-3">
                <MetalCardArt name={cleanName(p.name)} title={subtitle(p)} />
            </div>
            <h2 id="metal-promo-title" className="mt-4 text-lg font-bold text-gray-900">
                Upgrade to HexLink Metal
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-gray-500">
                Black stainless steel, laser-engraved with your name. It opens the same profile you already have, so there's no new setup fee.
            </p>
            <div className="mt-3 flex items-baseline gap-2">
                <span className="text-2xl font-bold text-gray-900">${METAL_PRICE}</span>
                <span className="font-techmono text-xs uppercase tracking-wider text-gray-500">per card</span>
            </div>
            <a
                href={metalUrl(user, 'sidebar')}
                target="_blank"
                rel="noopener"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700"
            >
                Order your metal card
                <ArrowRight className="h-4 w-4" />
            </a>
            <p className="mt-2 text-center text-xs text-gray-500">Free mockup to approve before we engrave.</p>
        </section>
    );
}

const DISMISS_KEY = 'n3d:metalPromoHidden';
const hiddenUntil = () => {
    try {
        return Number(localStorage.getItem(DISMISS_KEY)) || 0;
    } catch {
        return 0;
    }
};

// Slim banner for phones/tablets (the sidebar is hidden there). Can be hidden for 30 days.
export function MetalUpgradeBanner({ user, className = '' }) {
    const [hidden, setHidden] = useState(() => hiddenUntil() > Date.now());
    if (hidden) return null;
    const hide = () => {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now() + 30 * 864e5));
        } catch {
            /* private mode: hide for this visit only */
        }
        setHidden(true);
    };
    return (
        <div className={`n3d-promo relative flex items-center gap-3 rounded-2xl p-3 pr-10 ${className}`}>
            <div className="w-24 flex-none">
                <MetalCardArt name={cleanName(user?.profile?.name)} title="" />
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">New: HexLink Metal</p>
                <p className="text-xs text-gray-500">Laser-engraved steel. Same profile, no new setup fee.</p>
                <a href={metalUrl(user, 'banner')} target="_blank" rel="noopener" className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-primary-600">
                    Upgrade for ${METAL_PRICE}
                    <ArrowRight className="h-3.5 w-3.5" />
                </a>
            </div>
            <button type="button" onClick={hide} aria-label="Hide for now" className="absolute right-2 top-2 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                <X className="h-4 w-4" />
            </button>
        </div>
    );
}
